// カフェメニューモーダル。カフェ本体をタップすると開く。
//
// 6 種のドリンクを一覧表示する。未開発はシルエット＋コスト＋「開発」ボタン、
// 開発済みは属性表示＋「本日のスペシャル」トグル。開発・選択は純関数（menu.ts）で
// 処理し、結果を onChange で main に返して状態に反映する。DOM 直組み・タッチ一級。

import { DRINK_CATALOG } from '../sim/menu';
import { isDeveloped } from '../sim/menu';
import type { MenuState } from '../sim/menu';
import { ATTRIBUTE_LABELS } from '../sim/decos';

/** モーダルの開閉と再描画の窓口。 */
export interface MenuModal {
  /** 最新の状態（メニュー＋チップ残高）でモーダルを開く。 */
  open(menu: MenuState, chips: number): void;
  close(): void;
  readonly isOpen: boolean;
}

export interface MenuModalOptions {
  modalEl: HTMLElement; // #menuModal
  leadEl: HTMLElement; // #menuLead
  listEl: HTMLElement; // #menuList
  /** ドリンクを開発する（チップ消費）。実行後の最新 {menu,chips} が返る前提で再描画は main 主導。 */
  onDevelop: (drinkId: string) => void;
  /** 本日のスペシャルを選ぶ/解除する（トグル）。 */
  onSelectSpecial: (drinkId: string) => void;
}

export function createMenuModal(opts: MenuModalOptions): MenuModal {
  const { modalEl, leadEl, listEl, onDevelop, onSelectSpecial } = opts;

  // 開いている間の最新状態を保持して、操作のたびに即時再描画する。
  let curMenu: MenuState | null = null;
  let curChips = 0;

  function render(): void {
    if (!curMenu) return;
    const menu = curMenu;
    const developedCount = menu.developed.length;
    leadEl.textContent =
      developedCount === 0
        ? 'チップでドリンクを開発しよう。本日のスペシャルに選ぶと、その属性が庭に加わり、来店客のチップが少し増える。'
        : `開発済み ${developedCount} / ${DRINK_CATALOG.length} 種。本日のスペシャルを 1 つ選べる。`;

    listEl.innerHTML = '';
    for (const d of DRINK_CATALOG) {
      const developed = isDeveloped(menu, d.id);
      const isSpecial = menu.special === d.id;

      const row = document.createElement('div');
      row.className = 'menu-row' + (developed ? '' : ' locked');

      const cup = document.createElement('div');
      cup.className = 'cup';
      cup.textContent = d.icon;

      const info = document.createElement('div');
      info.className = 'info';
      const nm = document.createElement('div');
      nm.className = 'nm';
      nm.textContent = developed ? d.name : '???';
      const meta = document.createElement('div');
      meta.className = 'meta';
      if (developed) {
        meta.textContent =
          `誘引: ${ATTRIBUTE_LABELS[d.attribute]} +${d.attributeAmount}　チップ +${d.tipBonus}　` +
          d.flavor;
      } else {
        meta.textContent = `開発コスト 💰${d.cost}　— ${d.flavor}`;
      }
      info.append(nm, meta);

      const act = document.createElement('div');
      act.className = 'act';
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'pixel-btn';
      if (!developed) {
        btn.textContent = `開発 💰${d.cost}`;
        btn.disabled = curChips < d.cost;
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          onDevelop(d.id);
        });
      } else {
        btn.className = 'pixel-btn special' + (isSpecial ? ' on' : '');
        btn.textContent = isSpecial ? '★ 本日の' : 'スペシャル';
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          onSelectSpecial(d.id);
        });
      }
      act.appendChild(btn);

      row.append(cup, info, act);
      listEl.appendChild(row);
    }
  }

  function open(menu: MenuState, chips: number): void {
    curMenu = menu;
    curChips = chips;
    render();
    modalEl.classList.add('open');
  }
  function close(): void {
    modalEl.classList.remove('open');
    curMenu = null;
  }

  // 背景クリック / とじるボタンで閉じる。
  modalEl.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    if (t === modalEl || t.hasAttribute('data-close')) close();
  });

  return {
    open,
    close,
    get isOpen() {
      return modalEl.classList.contains('open');
    },
  };
}
